import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Role, Resource, Action, hasPermission, hasResourceAccess, mapKeycloakRole, getPermissionsForRole, Permission } from './permissions';

/**
 * RBAC Context Type
 */
interface RBACContextType {
  role: Role;
  permissions: Permission[];
  can: (resource: Resource, action: Action) => boolean;
  canAccess: (resource: Resource) => boolean;
  isAtLeast: (minimumRole: Role) => boolean;
  isLoading: boolean;
}

const RBACContext = createContext<RBACContextType | null>(null);

/**
 * RBAC Provider
 * Must be used inside React Admin context (after authentication)
 */
export function RBACProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<Role>(Role.VIEWER);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get permissions from localStorage (set by authProvider)
  useEffect(() => {
    const loadPermissions = () => {
      try {
        const userStr = localStorage.getItem('ekaa_admin_user');
        if (userStr) {
          const user = JSON.parse(userStr);
          const keycloakRoles = user.roles || [];
          const mappedRole = mapKeycloakRole(keycloakRoles);
          setRole(mappedRole);
          setPermissions(getPermissionsForRole(mappedRole));
        }
      } catch (error) {
        console.error('Error loading RBAC permissions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPermissions();

    // Listen for storage changes (e.g., login/logout)
    const handleStorageChange = () => loadPermissions();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const can = (resource: Resource, action: Action): boolean => {
    return hasPermission(role, resource, action);
  };

  const canAccess = (resource: Resource): boolean => {
    return hasResourceAccess(role, resource);
  };

  const isAtLeast = (minimumRole: Role): boolean => {
    const roleHierarchy = [Role.VIEWER, Role.SUPPORT, Role.FINANCE, Role.MODERATOR, Role.ADMIN, Role.SUPER_ADMIN];
    return roleHierarchy.indexOf(role) >= roleHierarchy.indexOf(minimumRole);
  };

  return (
    <RBACContext.Provider value={{ role, permissions, can, canAccess, isAtLeast, isLoading }}>
      {children}
    </RBACContext.Provider>
  );
}

/**
 * Default RBAC context (no permissions - everything hidden)
 */
const defaultRBACContext: RBACContextType = {
  role: Role.VIEWER,
  permissions: [],
  can: () => false,
  canAccess: () => false,
  isAtLeast: () => false,
  isLoading: true,
};

/**
 * Hook to access RBAC context
 * Returns default context if not within RBACProvider (safe fallback)
 */
export function useRBAC(): RBACContextType {
  const context = useContext(RBACContext);
  // Return default context if no provider (graceful degradation)
  return context || defaultRBACContext;
}

/**
 * Hook to check a specific permission
 */
export function useCan(resource: Resource, action: Action): boolean {
  const { can } = useRBAC();
  return can(resource, action);
}

/**
 * Hook to check resource access
 */
export function useCanAccess(resource: Resource): boolean {
  const { canAccess } = useRBAC();
  return canAccess(resource);
}

/**
 * Hook to get current role
 */
export function useRole(): Role {
  const { role } = useRBAC();
  return role;
}
